import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { drbfmService } from '@services/drbfm.service';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

interface PreviewTarget {
  id: string;
}

/**
 * Opens the dialog before the request resolves, so the user sees the loading
 * state rather than a delay with no feedback.
 */
export const useDrbfmHtmlPreview = <T extends PreviewTarget>() => {
  const { t } = useTranslation();
  const [session, setSession] = useState<T | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [html, setHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only the latest open() may write; close() and a later open() make earlier requests stale.
  const request = useRef(0);

  const open = useCallback(
    async (target: T) => {
      const id = ++request.current;
      setSession(target);
      setIsOpen(true);
      setIsLoading(true);
      setError(null);
      setHtml('');

      try {
        const html = await drbfmService.getSessionHTMLPreview(target.id);
        if (id === request.current) setHtml(html);
      } catch (err) {
        if (id === request.current) setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.history.previewError' }));
      } finally {
        if (id === request.current) setIsLoading(false);
      }
    },
    [t],
  );

  const close = useCallback(() => {
    request.current += 1;
    setIsOpen(false);
    setIsLoading(false);
    setSession(null);
    setHtml('');
    setError(null);
  }, []);

  return { session, isOpen, html, isLoading, error, open, close };
};
